// src/pages/Customer/CustomerRecipesPage.js (SIMPLIFIED for universal access via 'sharedRecipes')

import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../../firebase';
import { collection, query, getDocs, orderBy } from 'firebase/firestore'; 
import RecipeDetailModal from '../../components/RecipeDetailModal'; 
import '../../styles/ClientRecipes.css'; 

const CustomerRecipesPage = () => {
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [customerUID, setCustomerUID] = useState(null);

    // --- 1. Fetch ALL Recipes from the new SHARED collection ---
    // This function now performs a single, simple query against the flat collection.
    const fetchAllRecipes = useCallback(async () => {
        setLoading(true);
        try {
            // FIX: Query the new globally accessible 'sharedRecipes' collection
            const sharedRecipesRef = collection(db, 'sharedRecipes'); 
            const q = query(sharedRecipesRef, orderBy('uploadedAt', 'desc'));
            const querySnapshot = await getDocs(q);
            
            const fetchedRecipes = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Ensure timestamp is ready for the Detail Modal
                uploadedAt: doc.data().uploadedAt 
            }));

            setRecipes(fetchedRecipes);
        } catch (error) {
            // Log error, but rely on the empty state message for the user
            console.error("Error fetching shared recipes:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Effect for authentication and data fetch
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setCustomerUID(user.uid);
                // Call the new, simplified fetchAllRecipes function
                fetchAllRecipes();
            } else {
                setCustomerUID(null);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, [fetchAllRecipes]);

    // Handler to open Detail Modal when a card is clicked
    const handleCardClick = (recipe) => {
        setSelectedRecipe(recipe);
        setIsDetailModalOpen(true);
    };

    // Recipe Card Component (Inline, reused from previous version)
    const RecipeCard = ({ recipe }) => (
        <div className="recipe-card" onClick={() => handleCardClick(recipe)}>
            <h3 className="recipe-name">🍽️ {recipe.name}</h3>
            <p className="recipe-description">{recipe.description}</p>
            <div className="recipe-details-toggle">
                <p><strong>Ingredients:</strong> {recipe.ingredients?.length || 0} items</p>
                <span className="view-details-prompt">Click to view full recipe</span> 
            </div>
            <span className="recipe-uploaded-date">
                {recipe.uploadedAt && new Date(recipe.uploadedAt.toDate()).toLocaleDateString()}
            </span>
        </div>
    );

    return (
        <div className="client-recipes-manager-page">
            <div className="manager-header">
                <h1 className="page-title">Community Recipe Library</h1>
            </div>
            
            <p className="page-subtitle">View recipes uploaded by all dietitians and trainers.</p>

            <div className="recipes-list-container">
                {!customerUID && <p className="status-message">Please log in to view the recipes.</p>}
                {customerUID && loading && <p className="status-message">Loading all recipes...</p>}
                
                {!loading && customerUID && recipes.length === 0 && (
                    <div className="empty-state-card">
                        <p>The shared recipe library is currently empty. Check back later!</p>
                        <p className="text-sm mt-2">Recipes will appear here once a client has uploaded them.</p>
                    </div>
                )}
                
                {recipes.map(recipe => (
                    <RecipeCard key={recipe.id} recipe={recipe} />
                ))}
            </div>

            {/* Recipe Detail Modal (for viewing full recipe) */}
            <RecipeDetailModal
                isOpen={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                recipe={selectedRecipe}
            />
        </div>
    );
};

export default CustomerRecipesPage;
